import React, { useState, useEffect, useCallback } from 'react';
import { Box, Group, Text, Avatar, Skeleton, Paper, ScrollArea, UnstyledButton, Tooltip, ActionIcon } from '@mantine/core';
import { IconClock, IconUser, IconX } from '@tabler/icons-react';
import { photoService } from '../../services/entityServiceSingleton';

const STORAGE_KEY = 'hades_recent_people';
const MAX_RECENT = 10;

export interface RecentPerson {
    id: string;
    displayName: string;
    entityType: string;
    email?: string;
    viewedAt: number;
}

export function getRecentPeople(): RecentPerson[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

export function addRecentPerson(person: RecentPerson): void {
    try {
        const existing = getRecentPeople().filter(p => p.id !== person.id);
        const updated = [{ ...person, viewedAt: Date.now() }, ...existing].slice(0, MAX_RECENT);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        window.dispatchEvent(new CustomEvent('hades-recent-people-updated'));
    } catch { /* noop */ }
}

export function clearRecentPeople(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
        window.dispatchEvent(new CustomEvent('hades-recent-people-updated'));
    } catch { /* noop */ }
}

const SUBTYPE_COLORS: Record<string, string> = {
    employee: 'blue',
    student: 'green',
    contractor: 'orange',
    applicant: 'yellow',
    public: 'gray',
    sponsored: 'violet',
    shared: 'teal',
    vacancy: 'red',
};

function getInitials(name: string): string {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0][0]?.toUpperCase() || '?';
}

function MiniPersonCard({ person, onNavigate }: { person: RecentPerson; onNavigate: (hash: string) => void }) {
    const [photoData, setPhotoData] = useState<string | null>(null);
    const [photoLoaded, setPhotoLoaded] = useState(false);

    useEffect(() => {
        if (!person.id) {
            setPhotoLoaded(true);
            return;
        }
        const cached = photoService.getCachedPhoto(person.id);
        if (cached?.Photo) {
            setPhotoData(cached.Photo);
        }
        setPhotoLoaded(true);
    }, [person.id]);

    const color = SUBTYPE_COLORS[person.entityType.toLowerCase()] || 'gray';
    const hash = `#Person/${person.entityType.toLowerCase()}/view/${person.id}`;

    return (
        <UnstyledButton
            className="recent-person-chip"
            onClick={() => onNavigate(hash)}
            aria-label={`View ${person.displayName}`}
        >
            <Group gap={8} wrap="nowrap" align="center">
                {!photoLoaded ? (
                    <Skeleton circle width={32} height={32} />
                ) : photoData ? (
                    <Avatar src={`data:image/jpeg;base64,${photoData}`} size={32} radius="xl" />
                ) : (
                    <Avatar size={32} radius="xl" color={color}>
                        <Text size="xs" fw={600}>{getInitials(person.displayName)}</Text>
                    </Avatar>
                )}
                <Box style={{ minWidth: 0 }}>
                    <Text size="xs" fw={600} truncate="end" style={{ maxWidth: 120 }}>
                        {person.displayName}
                    </Text>
                    <Text size="10px" c="dimmed" truncate="end" style={{ maxWidth: 120 }}>
                        {person.entityType}
                    </Text>
                </Box>
            </Group>
        </UnstyledButton>
    );
}

export interface RecentPeopleStripProps {
    onNavigateHash?: (hash: string) => void;
}

export const RecentPeopleStrip: React.FC<RecentPeopleStripProps> = ({ onNavigateHash }) => {
    const [recentPeople, setRecentPeople] = useState<RecentPerson[]>([]);

    const refreshRecent = useCallback(() => {
        setRecentPeople(getRecentPeople());
    }, []);

    useEffect(() => {
        refreshRecent();
        const handler = () => refreshRecent();
        window.addEventListener('hades-recent-people-updated', handler);
        window.addEventListener('storage', handler);
        return () => {
            window.removeEventListener('hades-recent-people-updated', handler);
            window.removeEventListener('storage', handler);
        };
    }, [refreshRecent]);

    if (recentPeople.length === 0) return null;

    const handleNavigate = (hash: string) => {
        if (onNavigateHash) {
            onNavigateHash(hash);
        } else {
            window.location.hash = hash.startsWith('#') ? hash.substring(1) : hash;
        }
    };

    return (
        <Paper className="recent-people-strip" withBorder p="sm" radius="md" mb="md" mx={24}>
            <Group gap="xs" mb={8} align="center" justify="space-between">
                <Group gap="xs" align="center">
                    <IconClock size={14} style={{ opacity: 0.5 }} />
                    <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
                        Recent People
                    </Text>
                </Group>
                <Tooltip label="Clear recent history" position="left" withArrow>
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="xs"
                        onClick={clearRecentPeople}
                        aria-label="Clear recent people history"
                    >
                        <IconX size={14} />
                    </ActionIcon>
                </Tooltip>
            </Group>
            <ScrollArea type="hover" scrollbarSize={4}>
                <Group gap={8} wrap="nowrap">
                    {recentPeople.map((person) => (
                        <MiniPersonCard
                            key={person.id}
                            person={person}
                            onNavigate={handleNavigate}
                        />
                    ))}
                </Group>
            </ScrollArea>
        </Paper>
    );
};
